import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './StockView.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function StockView({ token }) {
  const [stock, setStock] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (token) {
      fetchStock();
    }
  }, [token, availabilityFilter]);

  const fetchStock = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/inventory/stock`;
      if (availabilityFilter) {
        url += `?availability=${availabilityFilter}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load stock inventory');
      }

      const data = await response.json();
      setStock(data.items || []);
      setSummary(data.summary || null);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (status) => {
    const labels = {
      available: 'Available',
      reserved: 'Reserved',
      delivered: 'Delivered'
    };
    return (
      <span className={`pallet-status status-${status}`}>
        {labels[status] || status}
      </span>
    );
  };

  const plStatusBadge = (status) => {
    const classes = {
      pending_data: 'badge-warning',
      complete: 'badge-info',
      partial: 'badge-primary',
      delivered: 'badge-success'
    };
    const labels = {
      pending_data: 'Pending Data',
      complete: 'In Stock',
      partial: 'Partial Delivery',
      delivered: 'Fully Delivered'
    };
    return (
      <span className={`pl-status-badge ${classes[status] || 'badge-secondary'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return <div className="stock-view-container"><div className="loading">Loading stock...</div></div>;
  }

  return (
    <div className="stock-view-container">
      <div className="stock-header">
        <div>
          <h1>Warehouse Stock</h1>
          <p>On-hand inventory by shipment, material, and pallet</p>
        </div>
        <div className="stock-filters">
          <select
            value={availabilityFilter}
            onChange={(e) => setAvailabilityFilter(e.target.value)}
          >
            <option value="">All stock</option>
            <option value="available">Has available stock</option>
            <option value="partial">Partially delivered</option>
            <option value="depleted">Fully depleted</option>
          </select>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {summary && (
        <div className="stock-summary-grid">
          <div className="summary-card">
            <h3>Packing Lists</h3>
            <p className="summary-number">{summary.packing_lists}</p>
          </div>
          <div className="summary-card">
            <h3>Total Pallets</h3>
            <p className="summary-number">{summary.total_pallets}</p>
          </div>
          <div className="summary-card available">
            <h3>Available</h3>
            <p className="summary-number">{summary.available}</p>
          </div>
          <div className="summary-card reserved">
            <h3>Reserved</h3>
            <p className="summary-number">{summary.reserved}</p>
          </div>
          <div className="summary-card delivered">
            <h3>Delivered</h3>
            <p className="summary-number">{summary.delivered}</p>
          </div>
        </div>
      )}

      {stock.length === 0 ? (
        <div className="no-stock">No stock records match the selected filter.</div>
      ) : (
        <div className="stock-list">
          {stock.map((item) => (
            <div key={item.packing_list_id} className="stock-card">
              <div
                className="stock-card-header"
                onClick={() => setExpandedId(expandedId === item.packing_list_id ? null : item.packing_list_id)}
              >
                <div className="stock-card-title">
                  <h3>
                    {item.order_number || item.invoice_number || `PL-${item.packing_list_id}`}
                  </h3>
                  <div className="stock-card-meta">
                    {item.supplier?.supplier_name && <span>{item.supplier.supplier_name}</span>}
                    {item.shipment?.client_name && <span>Client: {item.shipment.client_name}</span>}
                    {item.shipment?.sfr_number && <span>SFR: {item.shipment.sfr_number}</span>}
                  </div>
                </div>
                <div className="stock-card-badges">
                  {plStatusBadge(item.status)}
                  <span className={`availability-tag tag-${item.availability}`}>
                    {item.availability}
                  </span>
                  <span className="pallet-summary">
                    {item.summary?.available || 0} / {item.summary?.total_pallets || 0} pallets available
                  </span>
                </div>
              </div>

              {expandedId === item.packing_list_id && (
                <div className="stock-card-body">
                  {item.cargo_items?.length === 0 ? (
                    <p className="no-cargo">No cargo items entered yet.</p>
                  ) : (
                    item.cargo_items.map((cargo) => (
                      <div key={cargo.cargo_id} className="cargo-stock-block">
                        <div className="cargo-stock-header">
                          <h4>{cargo.material_description}</h4>
                          <div className="cargo-qty-summary">
                            <span><strong>{cargo.inventory?.quantity_remaining ?? 0}</strong> of {cargo.inventory?.quantity_pieces ?? 0} pcs remaining</span>
                            <span>{cargo.inventory?.pallets_available ?? 0} pallets available</span>
                          </div>
                        </div>
                        <div className="pallet-stock-grid">
                          {(cargo.inventory?.pallets || []).map((pallet) => (
                            <div key={pallet.pallet_number} className={`pallet-stock-item status-${pallet.status}`}>
                              <span className="pallet-id">{pallet.pallet_number}</span>
                              {statusBadge(pallet.status)}
                              <span className="pallet-qty">{pallet.quantity} pcs</span>
                              {pallet.delivery_number && (
                                <span className="pallet-ref">{pallet.delivery_number}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                  <div className="stock-card-actions">
                    <Link to={`/packing-list/${item.packing_list_id}/cargo`} className="btn btn-secondary btn-sm">
                      Manage Cargo
                    </Link>
                    {(item.summary?.available || 0) > 0 && (
                      <Link to="/delivery-notes/create" className="btn btn-primary btn-sm">
                        Create Delivery
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
